import {
  filter,
  fromPairs,
  mapValues,
  toArray,
  toPairs,
  toString,
} from "lodash-es";

import { TenantVendorRisk } from "@oneleet/api_client";

export type RiskOption = {
  id: string;
  label: string;
  risk: keyof typeof TenantVendorRisk;
};

export interface RiskAssessmentItemV1 {
  id: string;
  title: string;
  description?: string;
  type:
    | "YesNoItem"
    | "MultiSelectItem"
    | "RadioItem"
    | "CountriesItem"
    | "HighMediumLowItem"
    | "TextItem";
  options?: RiskOption[];
  selectedValueId?: string;
  selectedValueIds?: string[];
  textValue?: string;
  applicableWhen?: RiskAssessmentCondition;
  disabledInTemplate?: boolean;
}

/** @deprecated */
export interface RiskAssessmentItemV0 {
  id: string;
  title: string;
  description?: string;
  type:
    | "YesNoItem"
    | "MultiSelectItem"
    | "RadioItem"
    | "CountriesItem"
    | "HighMediumLowItem";
  options: RiskOption[];
  selectedValueId?: string;
  selectedValueIds?: string[];
  dependsOn?: { itemId: string; valueId: string };
}

export interface RiskAssessmentCategoryV1 {
  title: string;
  description?: string;
  items: RiskAssessmentItemV1[];
  applicableWhen?: RiskAssessmentCondition;
}

/** @deprecated */
export interface RiskAssessmentCategoryV0 {
  title: string;
  description: string;
  items: RiskAssessmentItemV0[];
  dependsOn?: { itemId: string; valueId: string };
}

export type RiskAssessmentFormV1 = {
  schemaVersion: 1;
  /** Date (y-m-d) when the questions, possible answers, etc. were last updated. */
  questionnaireVersion: RiskAssessmentQuestionnaireVersion;
  categories: RiskAssessmentCategoryV1[];
};

export type RiskAssessmentQuestionnaireVersion = "Legacy" | "2025-04-28";

/** @deprecated */
export type RiskAssessmentFormV0 = RiskAssessmentCategoryV0[];

export type RiskAssessmentForm = RiskAssessmentFormV0 | RiskAssessmentFormV1;

export function riskAssessmentIsV0(
  form: RiskAssessmentForm,
): form is RiskAssessmentFormV0 {
  if (Array.isArray(form)) {
    return true;
  }
  return !("schemaVersion" in form);
}
export function riskAssessmentIsV1(
  form: RiskAssessmentForm,
): form is RiskAssessmentFormV1 {
  return "schemaVersion" in form && form.schemaVersion === 1;
}

export function riskAssessmentQuestionnaireVersion(
  form: RiskAssessmentForm,
): RiskAssessmentQuestionnaireVersion {
  return riskAssessmentIsV1(form) ? form.questionnaireVersion : "Legacy";
}

export function riskAssessmentCategories(
  form: RiskAssessmentForm,
): RiskAssessmentCategoryV1[] {
  return riskAssessmentIsV1(form) ? form.categories : form;
}

/** Condition that determines whether a risk assessment item applies to a given vendor. */
export type RiskAssessmentCondition =
  | RiskAssessmentConditionNever
  | RiskAssessmentConditionAnd
  | RiskAssessmentConditionOr
  | RiskAssessmentConditionValueSelected;

/** Never applies. */
export type RiskAssessmentConditionNever = { condition: "never" };
/** Applies when all subconditions match.  */
export type RiskAssessmentConditionAnd = {
  condition: "and";
  subconditions: RiskAssessmentCondition[];
};
/** Applies when any subcondition matches. */
export type RiskAssessmentConditionOr = {
  condition: "or";
  subconditions: RiskAssessmentCondition[];
};
/** Applies when an item has a particular value selected. */
export type RiskAssessmentConditionValueSelected = {
  condition: "valueSelected";
  itemId: string;
  valueId: string;
};

/**
 * The schema for the vendor risk assessment form, JSON Schema format.
 */
export const VENDOR_SCHEMA = {
  $schema: "https://json-schema.org/draft/2020-12/schema",
  title: "Vendor Risk Assessment",
  description:
    "A vendor risk assessment form for evaluating the security of tenant's vendors.",
  type: "array",
  items: {
    type: "object",
    properties: {
      title: { type: "string" },
      description: { type: "string" },
      items: {
        type: "array",
        items: {
          oneOf: [
            { $ref: "#/definitions/RadioItem" },
            { $ref: "#/definitions/MultiSelectItem" },
            { $ref: "#/definitions/HighMediumLowItem" },
            { $ref: "#/definitions/YesNoItem" },
          ],
        },
      },
      dependsOn: {
        type: "object",
        properties: { itemId: { type: "string" }, valueId: { type: "string" } },
        required: ["itemId", "valueId"],
      },
    },
    required: ["title", "description", "items"],
    additionalProperties: false,
  },
  definitions: {
    BaseValue: {
      type: "object",
      properties: {
        id: { type: "string" },
        label: { type: "string" },
        risk: { type: "string", enum: ["LOW", "MEDIUM", "HIGH"] },
      },
      required: ["id", "risk"],
    },
    BaseItem: {
      type: "object",
      properties: {
        id: { type: "string" },
        title: { type: "string" },
        description: { type: "string" },
        dependsOn: {
          type: "object",
          properties: {
            itemId: { type: "string" },
            valueId: { type: "string" },
          },
          required: ["itemId", "valueId"],
        },
      },
      required: ["id", "title"],
    },
    RadioItem: {
      allOf: [
        { $ref: "#/definitions/BaseItem" },
        {
          properties: {
            type: { type: "string", const: "RadioItem" },
            options: {
              type: "array",
              items: { $ref: "#/definitions/BaseValue" },
            },
            selectedValueId: { type: "string" },
          },
          required: ["type", "options"],
        },
      ],
    },
    MultiSelectItem: {
      allOf: [
        { $ref: "#/definitions/BaseItem" },
        {
          properties: {
            type: { type: "string", const: "MultiSelectItem" },
            options: {
              type: "array",
              items: { $ref: "#/definitions/BaseValue" },
            },
            selectedValueIds: { type: "array", items: { type: "string" } },
          },
          required: ["type", "options"],
        },
      ],
    },
    HighMediumLowItem: {
      allOf: [
        { $ref: "#/definitions/BaseItem" },
        {
          properties: {
            type: { type: "string", const: "HighMediumLowItem" },
            options: {
              type: "array",
              minItems: 3,
              maxItems: 3,
              items: { $ref: "#/definitions/BaseValue" },
            },
            selectedValueId: {
              type: "string",
              enum: ["high", "medium", "low"],
            },
          },
          required: ["type", "options"],
        },
      ],
    },
    YesNoItem: {
      allOf: [
        { $ref: "#/definitions/BaseItem" },
        {
          properties: {
            type: { type: "string", const: "YesNoItem" },
            options: {
              type: "array",
              minItems: 2,
              maxItems: 2,
              items: { $ref: "#/definitions/BaseValue" },
            },
            selectedValueId: { type: "string", enum: ["yes", "no"] },
          },
          required: ["type", "options"],
        },
      ],
    },
  },
} as const;

export const RISK_ASSESSMENT_SPECIAL_FIELD_IDS = {
  PROCESSES_PII: "pii",
  DATA_DESCRIPTION: "data_description",
  CUSTOMER_DATA_IN_COUNTRIES: "customer_data_in_countries",
} as const;

export const DEFAULT_VENDOR_RISK_ASSESSMENT: RiskAssessmentFormV1 = {
  schemaVersion: 1,
  questionnaireVersion: "2025-04-28",
  categories: [
    {
      title: "Data Storage",
      items: [
        {
          id: "stores_data",
          title: "Does this vendor store data?",
          type: "YesNoItem",
          options: [
            { id: "yes", label: "Yes", risk: "LOW" },
            { id: "no", label: "No", risk: "LOW" },
          ],
          disabledInTemplate: true,
        },
        {
          id: "customer_or_company_data",
          title: "What data does this vendor store?",
          applicableWhen: {
            condition: "valueSelected",
            itemId: "stores_data",
            valueId: "yes",
          },
          type: "MultiSelectItem",
          options: [
            { id: "customer", label: "Customer-owned data", risk: "MEDIUM" },
            { id: "company", label: "Company-owned data", risk: "LOW" },
          ],
          disabledInTemplate: true,
        },
      ],
    },
    {
      title: "Data Storage – General",
      applicableWhen: {
        condition: "valueSelected",
        itemId: "stores_data",
        valueId: "yes",
      },
      items: [
        {
          id: RISK_ASSESSMENT_SPECIAL_FIELD_IDS.DATA_DESCRIPTION,
          title: "Describe the data that is stored on this vendor's services:",
          type: "TextItem",
          disabledInTemplate: true,
        },
        {
          id: "encrypt_in_transit",
          title: "Does the vendor encrypt sensitive data in transit?",
          type: "YesNoItem",
          options: [
            { id: "yes", label: "Yes", risk: "LOW" },
            { id: "no", label: "No", risk: "HIGH" },
          ],
        },
        {
          id: "encrypt_at_rest",
          title: "Does the vendor encrypt sensitive data at rest?",
          type: "YesNoItem",
          options: [
            { id: "yes", label: "Yes", risk: "LOW" },
            { id: "no", label: "No", risk: "HIGH" },
          ],
        },
        {
          id: "retention_deletion_process",
          title:
            "Does the vendor have a formal data retention and deletion process?",
          type: "YesNoItem",
          options: [
            { id: "yes", label: "Yes", risk: "LOW" },
            { id: "no", label: "No", risk: "MEDIUM" },
          ],
        },
      ],
    },
    {
      title: "Data Storage – Customer Data",
      applicableWhen: {
        condition: "valueSelected",
        itemId: "customer_or_company_data",
        valueId: "customer",
      },
      items: [
        {
          id: RISK_ASSESSMENT_SPECIAL_FIELD_IDS.PROCESSES_PII,
          title:
            "Does this customer-owned data contain personally identifiable information?",
          type: "YesNoItem",
          options: [
            { id: "yes", label: "Yes", risk: "MEDIUM" },
            { id: "no", label: "No", risk: "LOW" },
          ],
          disabledInTemplate: true,
        },
        {
          id: RISK_ASSESSMENT_SPECIAL_FIELD_IDS.CUSTOMER_DATA_IN_COUNTRIES,
          title: "In what countries does the vendor store customer data?",
          type: "CountriesItem",
          disabledInTemplate: true,
        },
      ],
    },
    {
      title: "Information Security Practices",
      items: [
        {
          id: "security_program",
          title: "Does this vendor have a formal information security program?",
          type: "RadioItem",
          options: [
            { id: "yes", label: "Yes", risk: "LOW" },
            { id: "no", label: "No", risk: "HIGH" },
            { id: "in_progress", label: "In-progress", risk: "MEDIUM" },
          ],
        },
        {
          id: "review_or_pentest",
          title:
            "When was the last independent security assessment or penetration test conducted?",
          type: "RadioItem",
          options: [
            { id: "last_6_mo", label: "Last 6 months", risk: "LOW" },
            { id: "6_to_12_mo_ago", label: "6-12 months ago", risk: "LOW" },
            { id: "over_12_mo_ago", label: "Over 12 months ago", risk: "LOW" },
            { id: "never", label: "Never", risk: "HIGH" },
            { id: "unknown", label: "Unknown", risk: "LOW" },
          ],
        },
        {
          id: "time_to_notify",
          title:
            "How quickly does the vendor commit to notifying customers of security incidents that affect their data?",
          type: "RadioItem",
          options: [
            { id: "within_24h", label: "Within 24 hours", risk: "LOW" },
            { id: "within_48h", label: "Within 48 hours", risk: "LOW" },
            { id: "within_72h", label: "Within 72 hours", risk: "LOW" },
            { id: "contract", label: "As required by contract", risk: "LOW" },
            { id: "none", label: "No formal commitment", risk: "HIGH" },
          ],
        },
        {
          id: "rbac",
          title: "Does this vendor allow for role-based access controls?",
          type: "YesNoItem",
          options: [
            { id: "yes", label: "Yes", risk: "LOW" },
            { id: "no", label: "No", risk: "HIGH" },
          ],
        },
        {
          id: "mfa",
          title: "Does this vendor allow for multi-factor authentication?",
          type: "RadioItem",
          options: [
            { id: "required", label: "Yes, required", risk: "LOW" },
            { id: "optional", label: "Yes, optional", risk: "LOW" },
            { id: "no", label: "No", risk: "HIGH" },
          ],
        },
      ],
    },
  ],
} as const;

export const conditionPasses = (
  form: RiskAssessmentFormV1,
  selectedItems: Record<string, string | string[]>,
  condition?: RiskAssessmentCondition,
): boolean => {
  if (!condition) return true;

  switch (condition.condition) {
    case "never":
      return false;
    case "and":
      return condition.subconditions.every((subcondition) =>
        conditionPasses(form, selectedItems, subcondition),
      );
    case "or":
      return condition.subconditions.some((subcondition) =>
        conditionPasses(form, selectedItems, subcondition),
      );
    case "valueSelected": {
      for (const category of form.categories) {
        for (const item of category.items) {
          if (item.id === condition.itemId) {
            // NOTE: Take care not to define circular conditions!
            if (
              !conditionPasses(form, selectedItems, category.applicableWhen) ||
              !conditionPasses(form, selectedItems, item.applicableWhen)
            ) {
              // Either it doesn't exist (it should!), or it's disabled (fair enough)
              return false;
            }
          }
        }
      }

      const value = selectedItems[condition.itemId];
      return (
        value === condition.valueId ||
        (Array.isArray(value) && value.includes(condition.valueId))
      );
    }
  }
};

/**
 * Checks if all fields in the form are completed, excluding fields and categories
 * that do not apply.
 *
 * @param data - The form data.
 * @returns True if all fields are completed, false otherwise.
 */
export const allFieldsCompleted = (data: RiskAssessmentFormV1): boolean => {
  const selectedItems: Record<string, string | string[]> =
    getSelectedItems(data);

  const isItemCompleted = (item: RiskAssessmentItemV1): boolean => {
    if (
      item.applicableWhen &&
      !conditionPasses(data, selectedItems, item.applicableWhen)
    ) {
      // Non-applicable item
      return true;
    }

    return itemHasValue(item);
  };

  for (const category of riskAssessmentCategories(data)) {
    if (
      category.applicableWhen &&
      !conditionPasses(data, selectedItems, category.applicableWhen)
    ) {
      // Non-applicable category
      continue;
    }

    for (const item of category.items) {
      if (!isItemCompleted(item)) {
        return false;
      }
    }
  }

  return true;
};

/**
 * Checks whether a form field has been filled.
 *
 * This does not account for whether the field is applicable to the overall form.
 */
export const itemHasValue = (item: RiskAssessmentItemV1) => {
  switch (item.type) {
    case "YesNoItem":
    case "RadioItem":
    case "HighMediumLowItem":
      return !!item.selectedValueId && item.selectedValueId !== "";
    case "MultiSelectItem":
      return (
        // Allow no-item selections for this question in the legacy (prior to May 2025) questionnaire
        item.id === "asset_storage_type" ||
        (Array.isArray(item.selectedValueIds) &&
          item.selectedValueIds.length > 0)
      );
    case "CountriesItem":
      return (
        Array.isArray(item.selectedValueIds) && item.selectedValueIds.length > 0
      );
    case "TextItem":
      return !!item.textValue?.trim();
    default:
      return false;
  }
};

/**
 * Suggests the overall risk level of a vendor based on specific questions in the assessment data.
 * The function checks the risk levels of predefined questions and uses them to determine the overall risk.
 *
 * @param assessmentData - An array of objects representing different categories of risk assessment questions and their items.
 * @returns The overall risk level as a string ('low', 'medium', or 'high').
 */
export const suggestRiskLevel = (
  assessmentData: RiskAssessmentForm | undefined,
): TenantVendorRisk => {
  if (!assessmentData) return TenantVendorRisk.LOW;

  const allItems = riskAssessmentCategories(assessmentData).flatMap(
    (category) => category.items,
  );

  // Calculate the overall risk level based on the risk levels of the selected answers
  const riskLevels = allItems.map((item) => {
    const options = item.options ?? [];

    if (
      item.type === "RadioItem" ||
      item.type === "YesNoItem" ||
      item.type === "CountriesItem"
    ) {
      return options.find((o) => o.id === item.selectedValueId)?.risk;
    } else if (item.type === "MultiSelectItem") {
      return item.selectedValueIds
        ?.map((id) => options.find((o) => o.id === id)?.risk)
        .sort()
        .pop();
    } else if (item.type === "HighMediumLowItem") {
      return item.selectedValueId;
    }
    return undefined;
  });

  if (riskLevels.filter((r) => r === "HIGH").length > 2) {
    return TenantVendorRisk.HIGH;
  } else if (riskLevels.includes("HIGH")) {
    return TenantVendorRisk.MEDIUM;
  }
  return TenantVendorRisk.LOW;
};

/**
 * Updates the risk assessment form by setting a selected value for a specific item.
 * This function identifies the item by its ID within the form and updates it with the provided selected value.
 * For 'MultiSelectItem' types, the selected value should be an array of strings, while other item types should receive a single string value.
 *
 * @param currentForm - The current state of the risk assessment form, structured as an array of categories, each containing an array of items.
 * @param itemId - The unique identifier for the item to be updated within the form.
 * @param selectedValue - The selected value to be set for the item. This can be a single string for single-select items or an array of strings for multi-select items.
 * @returns A new instance of the risk assessment form with the updated selected value for the specified item.
 */
export const setRiskAssessmentFormValue = (
  currentForm: RiskAssessmentFormV1,
  itemId: string,
  selectedValue?: string | string[],
): RiskAssessmentFormV1 => {
  return {
    ...currentForm,
    categories: riskAssessmentCategories(currentForm).map((category) => ({
      ...category,
      items: category.items.map((item): RiskAssessmentItemV1 => {
        if (item.id === itemId) {
          switch (item.type) {
            case "YesNoItem":
              return { ...item, selectedValueId: toString(selectedValue) };
            case "MultiSelectItem":
              return { ...item, selectedValueIds: toArray(selectedValue) };
            case "RadioItem":
              return { ...item, selectedValueId: toString(selectedValue) };
            case "CountriesItem":
              return { ...item, selectedValueIds: toArray(selectedValue) };
            case "HighMediumLowItem":
              return { ...item, selectedValueId: toString(selectedValue) };
            case "TextItem":
              return { ...item, textValue: toString(selectedValue) };
          }
        }
        return item;
      }),
    })),
  };
};

/**
 * Retrieves an object mapping each selected item's ID to its selected value(s).
 *
 * @param form - The risk assessment form data.
 * @returns An object mapping item IDs to their selected value(s).
 */
export const getSelectedItems = (
  form: RiskAssessmentForm,
): Record<string, string | string[]> => {
  // We can't use lodash `chain()` because we tree-shake the module
  // https://github.com/lodash/lodash/issues/4712
  const items = getSelectedOrUndefinedItems(form);
  const pairs = toPairs(items);
  const filteredPairs = filter(pairs, ([, value]) => value !== undefined);
  return fromPairs(filteredPairs) as Record<string, string | string[]>;
};

export const getSelectedOrUndefinedItems = (
  form: RiskAssessmentForm,
): Record<string, string | string[] | undefined> => {
  const selectedItems: Record<string, string | string[] | undefined> = {};

  riskAssessmentCategories(form).forEach((category) => {
    category.items.forEach((item) => {
      selectedItems[item.id] = getItemValue(item);
    });
  });

  return selectedItems;
};

export const getItemValue = (
  item: RiskAssessmentItemV1,
): string | string[] | undefined => {
  switch (item.type) {
    case "YesNoItem":
      return item.selectedValueId;
    case "MultiSelectItem":
      return item.selectedValueIds;
    case "RadioItem":
      return item.selectedValueId;
    case "CountriesItem":
      return item.selectedValueIds;
    case "HighMediumLowItem":
      return item.selectedValueId;
    case "TextItem":
      return item.textValue;
  }
};

const getFieldsAndCategoriesById = (
  form: RiskAssessmentForm,
): Record<
  string,
  { field: RiskAssessmentItemV1; category: RiskAssessmentCategoryV1 }
> => {
  const fieldsById: Record<
    string,
    { field: RiskAssessmentItemV1; category: RiskAssessmentCategoryV1 }
  > = {};

  riskAssessmentCategories(form).forEach((category) => {
    category.items.forEach((item) => {
      fieldsById[item.id] = { field: item, category };
    });
  });

  return fieldsById;
};

export const getConditionsByFieldId = (
  form: RiskAssessmentForm,
): Record<string, RiskAssessmentCondition[]> => {
  const fieldsAndCategoriesById = getFieldsAndCategoriesById(form);

  return mapValues(fieldsAndCategoriesById, (fieldAndCategory) =>
    [
      fieldAndCategory.category.applicableWhen
        ? [fieldAndCategory.category.applicableWhen]
        : [],
      fieldAndCategory.field.applicableWhen
        ? [fieldAndCategory.field.applicableWhen]
        : [],
    ].flat(),
  );
};
